/**
 * The Quoting module's permission vocabulary.
 *
 * The module owns this — the OS does not dictate which tiers a module offers. Wire
 * strings are derived (`quoting:view`, …), so nothing here or at any call site writes
 * one by hand.
 */
import { defineModulePermissions } from '@noissue/authz';

export const QUOTING = defineModulePermissions({
  id: 'quoting',
  name: 'Quoting',
  icon: 'calculator',
  permissions: {
    view: {
      label: 'View quotes',
      description: 'See quotes and the product catalogue.',
    },
    edit: {
      label: 'Create and edit quotes',
      description: 'Start, configure and duplicate quotes.',
    },
    manage: {
      label: 'Manage quoting',
      description: 'Change quote types, pricing configuration and module settings.',
    },
    /**
     * Deliberately OFF-LADDER: approval authority is orthogonal to editing, not more of
     * it. A finance approver may never edit a quote, and someone who edits quotes should
     * not thereby be able to sign off on their own discounts.
     *
     * Declared ahead of its enforcement: the submit gate that compares the returned
     * margin against the quote type's approval threshold lands with the approval flow
     * (quoting-build-plan §10). Until then this is grantable but not yet checked.
     */
    approve: {
      label: 'Approve quotes',
      description: 'Sign off on quotes whose margin falls below the approval threshold.',
    },
    /**
     * OFF-LADDER, like `approve`: Logistics answers freight RFQs (§12) without
     * needing to edit quotes, and editing quotes does not make anyone Logistics.
     */
    freight: {
      label: 'Answer freight RFQs',
      description:
        'Work the Freight RFQs queue for Logistics and answer them with a freight price.',
    },
    /** OFF-LADDER, likewise: Sourcing answers product RFQs (§12) — prices the catalogue lacks. */
    sourcing: {
      label: 'Answer product RFQs',
      description:
        'Work the Products RFQs queue for Sourcing and answer them with supplier pricing.',
    },
  },
  visibility: 'view',
  levels: [
    { key: 'view', label: 'View', grants: ['view'] },
    { key: 'edit', label: 'Edit', grants: ['view', 'edit'] },
    { key: 'manage', label: 'Manage', grants: ['view', 'edit', 'manage'] },
  ],
});
